import { Link } from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import { useEffect, useState, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { tryApiRequest, apiRequest, getAuthUser, uploadFiles, getApiCacheSync, invalidateApiCache, type BackendProduct, API_BASE } from '../lib/api';
import EditablePageHeader from '../components/EditablePageHeader';

const SLUG_IMAGES: Record<string, string> = {
  'bmcu-standard': 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
  'bmcu-pro': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
  '3d-accessories': 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=800&q=80',
};

function productImage(slug: string) {
  return SLUG_IMAGES[slug] ?? 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80';
}

function formatPrice(val: string) {
  if (!val) return 'Liên hệ';
  const num = Number(val);
  if (!isNaN(num)) {
    return num.toLocaleString('vi-VN') + ' VNĐ';
  }
  return val;
}

const emptyProduct = {
  name: '',
  slug: '',
  priceDisplay: 'Liên hệ',
  description: '',
  ecommerceLink: 'https://3dsmart.local',
  buttonText: 'Mua Ngay',
  isActive: true,
  images: '[]',
  features: '',
  specs: '[]',
};

function makeSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function ProductsPage() {
  const user = getAuthUser();
  const isAdmin = user?.role === 'ADMIN';
  const cachedData = getApiCacheSync<{ products: BackendProduct[] }>('/products');
  const [products, setProducts] = useState<BackendProduct[]>(cachedData?.products || []);
  const [loading, setLoading] = useState(!cachedData);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  
  // Dynamic specs state
  const [specList, setSpecList] = useState<{key: string, value: string}[]>([]);

  async function loadProducts() {
    setLoading(true);
    const response = await tryApiRequest<{ products: BackendProduct[] }>('/products', { useCache: true });
    setProducts(response?.products ?? []);
    setLoading(false);
  }

  useEffect(() => {
    loadProducts();
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
      const newPreviews = filesArray.map(f => URL.createObjectURL(f));
      setPreviewUrls((prev) => [...prev, ...newPreviews]);
    }
  }

  function removePreview(index: number, isExisting: boolean) {
    if (isExisting) {
      const existingImages = JSON.parse(form.images || '[]');
      existingImages.splice(index, 1);
      setForm({ ...form, images: JSON.stringify(existingImages) });
    } else {
      const newFiles = [...selectedFiles];
      newFiles.splice(index, 1);
      setSelectedFiles(newFiles);
      
      const newPreviews = [...previewUrls];
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      setPreviewUrls(newPreviews);
    }
  }

  async function submitProduct(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setStatus('Đang tải lên ảnh...');
    
    try {
      let finalImages = JSON.parse(form.images || '[]');
      
      if (selectedFiles.length > 0) {
        const uploadedUrls = await uploadFiles(selectedFiles);
        finalImages = [...finalImages, ...uploadedUrls];
      }

      setStatus('Đang lưu sản phẩm...');
      const payload = {
        ...form,
        slug: form.slug || makeSlug(form.name),
        images: JSON.stringify(finalImages),
        specs: JSON.stringify(specList),
        features: form.features || undefined,
      };

      if (editingId) {
        const res = await apiRequest<{ product: BackendProduct }>(`/products/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        setProducts(prev => prev.map(p => p.id === editingId ? res.product : p));
      } else {
        const res = await apiRequest<{ product: BackendProduct }>('/products', { method: 'POST', body: JSON.stringify(payload) }, true);
        setProducts(prev => [res.product, ...prev]);
      }
      
      setShowModal(false);
      setEditingId('');
      setForm(emptyProduct);
      setSelectedFiles([]);
      setPreviewUrls([]);
      setSpecList([]);
      setStatus('');
      invalidateApiCache('/products');
      // Optimistic update: state already updated from server response, no reload needed
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Không thể lưu sản phẩm');
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(id: string) {
    setDeleteId(id);
  }

  async function executeDelete() {
    if (!deleteId) return;
    const targetId = deleteId;
    setDeleteId(null);
    setProducts(prev => prev.filter(p => p.id !== targetId));
    try {
      await apiRequest(`/products/${targetId}`, { method: 'DELETE' }, true);
      invalidateApiCache('/products');
      // Optimistic update: item already removed from state, no re-fetch needed
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Không thể xóa');
      await loadProducts(); // Rollback on error
    }
  }

  function openAdd() {
    setEditingId('');
    setForm(emptyProduct);
    setSlugEdited(false);
    setStatus('');
    setSelectedFiles([]);
    setPreviewUrls([]);
    setSpecList([]);
    setShowModal(true);
  }

  function openEdit(product: BackendProduct) {
    setEditingId(product.id);
    setForm({
      name: product.name,
      slug: product.slug,
      priceDisplay: product.priceDisplay,
      description: product.description,
      ecommerceLink: product.ecommerceLink || '',
      buttonText: product.buttonText || 'Mua Ngay',
      images: product.images || '[]',
      isActive: product.isActive,
      features: product.features || '',
      specs: product.specs || '[]',
    });
    setSlugEdited(true);
    setSelectedFiles([]);
    setPreviewUrls([]);
    try {
      setSpecList(JSON.parse(product.specs || '[]'));
    } catch {
      setSpecList([]);
    }
    setStatus('');
    setShowModal(true);
  }

  function addSpecRow() {
    setSpecList([...specList, { key: '', value: '' }]);
  }

  function updateSpec(index: number, field: 'key' | 'value', val: string) {
    const updated = [...specList];
    updated[index][field] = val;
    setSpecList(updated);
  }

  function removeSpec(index: number) {
    const updated = [...specList];
    updated.splice(index, 1);
    setSpecList(updated);
  }

  const visibleProducts = isAdmin ? products : products.filter(p => p.isActive);

  return (
    <div className="page-block">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }}>
        <EditablePageHeader pageKey="products" defaultEyebrow="Sản phẩm" defaultTitle="Danh mục sản phẩm" />
      </motion.div>

      {loading && products.length === 0 ? (
        <div style={{ minHeight: '100vh' }} />
      ) : (visibleProducts.length > 0 || isAdmin) ? (
        <div className="product-grid" style={{ marginTop: '32px' }}>
          {isAdmin && (
            <motion.article 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.15 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="product-card glass-card" 
              onClick={openAdd}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '380px',
                border: '2px dashed rgba(255, 255, 255, 0.18)',
                background: 'rgba(255, 255, 255, 0.02)',
                boxShadow: 'none',
                transition: 'all 0.25s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'; e.currentTarget.style.borderColor = 'var(--neon-red)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)'; }}
            >
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '32px', color: 'var(--neon-red)', marginBottom: '16px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                boxShadow: '0 0 15px rgba(239, 68, 68, 0.2)'
              }}>
                +
              </div>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#f8fafc', fontWeight: 700 }}>Thêm sản phẩm</h3>
            </motion.article>
          )}
          {visibleProducts.map((product, idx) => {
            let coverImg = productImage(product.slug);
            try {
              const imgs = JSON.parse(product.images || '[]');
              if (imgs.length > 0) coverImg = API_BASE.replace('/api', '') + imgs[0];
            } catch {}
            
            return (
              <motion.article
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.15 }}
                transition={{ duration: 0.5, delay: idx * 0.08, ease: 'easeOut' }}
                className="product-card glass-card"
              >
                <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', overflow: 'hidden' }}>
                  <div
                    className="product-image"
                    style={{ backgroundImage: `url("${coverImg}")`, opacity: product.isActive ? 1 : 0.5, transition: 'transform 0.5s ease' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </Link>
                <div className="product-body" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <h3 style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      margin: '0 0 12px 0',
                      lineHeight: '1.4',
                      color: '#f1f5f9'
                    }}>
                      {product.name} {isAdmin && !product.isActive && <span style={{ color: 'red', fontSize: '0.8em' }}>(Đã ẩn)</span>}
                    </h3>
                  </Link>
                  <div className="inline-info" style={{ marginTop: 'auto', marginBottom: '16px' }}>
                    <strong style={{ fontSize: '1.25rem', color: 'var(--neon-red)' }}>{formatPrice(product.priceDisplay)}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link 
                      className="button primary small glow-border" 
                      style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }} 
                      to={product.ecommerceLink && product.ecommerceLink !== 'https://' ? product.ecommerceLink : `/products/${product.slug}`} 
                      target={product.ecommerceLink && product.ecommerceLink !== 'https://' ? "_blank" : undefined}
                      onClick={() => {
                        fetch(`${API_BASE}/analytics/event`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ eventType: 'BUY_CLICK', targetId: product.id, targetName: product.name }),
                          keepalive: true
                        }).catch(e => console.error('Tracking failed', e));
                      }}
                    >
                      {product.buttonText || 'Mua Ngay'}
                    </Link>
                    {isAdmin && (
                      <>
                        <button className="button secondary small glow-border" style={{ padding: '6px 14px', borderRadius: '10px' }} onClick={() => openEdit(product)}>
                          Sửa
                        </button>
                        <button style={{ padding: '6px 14px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', cursor: 'pointer', fontWeight: 600 }} onClick={() => confirmDelete(product.id)}>
                          Xóa
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      ) : (
        <div className="glass-card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', marginTop: '32px' }}>
          <p>Chưa có sản phẩm nào.</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2 style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px', color: '#f8fafc', fontSize: '20px', fontWeight: 700 }}>{editingId ? 'Sửa thông tin sản phẩm' : 'Thêm sản phẩm mới'}</h2>
            
            <form className="admin-form" onSubmit={submitProduct}>
              {status && <p className="auth-status" style={{ color: 'red' }}>{status}</p>}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="field-label">Tên sản phẩm <span style={{color: 'red'}}>*</span></label>
                  <input className="input-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugEdited ? form.slug : makeSlug(e.target.value) })} required placeholder="Nhập tên sản phẩm" />
                </div>
                
                <div>
                  <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Đường dẫn tĩnh (Slug) <span style={{color: 'red'}}>*</span></span>
                    <button type="button" className="reset-slug-btn" onClick={() => { setSlugEdited(false); setForm({ ...form, slug: makeSlug(form.name) }); }} title="Tạo lại từ Tiêu đề">⟲ Auto</button>
                  </label>
                  <input className="input-box" value={form.slug} onChange={(e) => { setSlugEdited(true); setForm({ ...form, slug: e.target.value }); }} required placeholder="vi-du-san-pham" />
                </div>
                
                <div>
                  <label className="field-label">Giá hiển thị <span style={{color: 'red'}}>*</span></label>
                  <input className="input-box" value={form.priceDisplay} onChange={(e) => setForm({ ...form, priceDisplay: e.target.value })} required placeholder="VD: 500.000 VNĐ hoặc Liên hệ" />
                </div>
                
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <label className="field-label">Loại nút bấm <span style={{color: 'red'}}>*</span></label>
                    <select className="input-box" value={form.buttonText} onChange={(e) => setForm({ ...form, buttonText: e.target.value })}>
                      <option value="Mua Ngay">Mua Ngay</option>
                      <option value="Liên hệ">Liên hệ</option>
                    </select>
                  </div>
                  <div style={{ flex: 2 }}>
                    <label className="field-label">Link thao tác (Link Shopee, Zalo, FB, ...)</label>
                    <input className="input-box" type="url" value={form.ecommerceLink} onChange={(e) => setForm({ ...form, ecommerceLink: e.target.value })} required placeholder="https://" />
                  </div>
                </div>
                
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="field-label">Mô tả ngắn <span style={{color: 'red'}}>*</span></label>
                  <textarea className="input-box textarea" style={{ minHeight: '80px' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Viết mô tả ngắn gọn..." />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px', flexWrap: 'wrap', gap: '6px' }}>
                    <label className="field-label" style={{ margin: 0 }}>Ảnh sản phẩm (Tải lên)</label>
                    <span style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: 500 }}>
                      📐 Khuyến nghị: Hình vuông 1:1 (VD: 800×800px hoặc 1000×1000px, tối đa 5MB)
                    </span>
                  </div>
                  <div style={{ position: 'relative', border: '2px dashed rgba(255,255,255,0.18)', borderRadius: '12px', padding: '24px 16px', textAlign: 'center', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', transition: 'all 0.2s', marginTop: '6px' }}
                       onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--neon-red)'; e.currentTarget.style.background = 'rgba(225,29,72,0.05)'; }}
                       onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  >
                    <input type="file" multiple accept="image/*" onChange={handleFileChange} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} title="Tải ảnh lên" />
                    <div style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
                      <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>📸</span>
                      <strong style={{ color: '#fff' }}>Nhấp để chọn ảnh</strong> hoặc kéo thả tệp vào đây
                      <p style={{ margin: '8px 0 0', fontSize: '0.82rem', color: '#38bdf8' }}>
                        Khuyến nghị: Ảnh tỉ lệ vuông 1:1 để hiển thị đồng bộ đẹp nhất ở cả ngoài danh sách và chi tiết
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
                    {/* Existing Images */}
                    {(() => {
                      try {
                        const existing = JSON.parse(form.images || '[]');
                        return existing.map((img: string, idx: number) => (
                          <div key={'ext'+idx} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)' }}>
                            <img src={API_BASE.replace('/api', '') + img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" />
                            <button type="button" onClick={() => removePreview(idx, true)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(225,29,72,0.85)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                          </div>
                        ));
                      } catch { return null; }
                    })()}
                    
                    {/* New Uploading Previews */}
                    {previewUrls.map((url, idx) => (
                      <div key={'new'+idx} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '2px dashed #4ade80' }}>
                        <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" />
                        <button type="button" onClick={() => removePreview(idx, false)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(225,29,72,0.85)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="field-label">Tính năng & Chính sách (Mỗi dòng 1 tính năng)</label>
                  <textarea className="input-box textarea" style={{ minHeight: '80px' }} value={form.features || ''} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder={`Sản phẩm phân phối chính hãng\nHỗ trợ kỹ thuật 24/7`} />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="field-label">Thông tin chi tiết (Specs)</label>
                  <div style={{ background: 'rgba(255,255,255,0.03)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {specList.map((spec, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <input className="input-box" placeholder="Tên (VD: Điện áp)" value={spec.key} onChange={e => updateSpec(idx, 'key', e.target.value)} style={{ flex: 1 }} />
                        <input className="input-box" placeholder="Giá trị (VD: 24V)" value={spec.value} onChange={e => updateSpec(idx, 'value', e.target.value)} style={{ flex: 2 }} />
                        <button type="button" onClick={() => removeSpec(idx)} className="button secondary small" style={{ padding: '0 12px', color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.4)', background: 'rgba(239, 68, 68, 0.1)' }}>Xoá</button>
                      </div>
                    ))}
                    <button type="button" onClick={addSpecRow} className="button secondary small">+ Thêm dòng thông số</button>
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '20px' }}>
                <label className="check-toggle">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  Trạng thái: Đang kinh doanh (Hiển thị ra ngoài trang chủ)
                </label>
              </div>
              
              <div className="modal-actions">
                <button className="button outline" type="button" onClick={() => setShowModal(false)}>Hủy bỏ</button>
                <button className="button primary" type="submit" disabled={submitting} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  {submitting && <span className="button-spinner" />}
                  {submitting ? (editingId ? 'Đang cập nhật...' : 'Đang lưu...') : (editingId ? 'Cập nhật' : 'Lưu sản phẩm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        message="Bạn có chắc chắn muốn xóa sản phẩm này không? Hành động này không thể hoàn tác."
        onCancel={() => setDeleteId(null)}
        onConfirm={executeDelete}
      />
    </div>
  );
}
